/**
 * 模型参数 Schema 配置
 *
 * 每个模型定义其参数列表，供通用渲染组件和生成 Mixin 使用。
 * 所有参数均从现有模型详情页提取，保持完整性和准确性。
 *
 * 参数字段说明：
 *   key           - 参数键名（对应 data 中的变量名和 params 中的字段名）
 *   type          - 控件类型：button-group | slider | switch | number-input | size-grid | upload | text-input | card-select | resolution-chips | custom-size | hidden
 *   label         - 显示标签（i18n key 或直接文字）
 *   options       - 可选项数组（button-group / size-grid / card-select / resolution-chips 使用）
 *   default       - 默认值
 *   showInCompact - 是否在首页简版参数中显示
 *   condition     - 显示条件函数，接收所有参数值的对象，返回 true 时显示
 *   props         - 控件额外属性（如 min, max, step, accept, maxlength 等）
 *   mapTo         - 生成时映射到 params 的字段名（不设置则用 key 本身）
 *   mapValue      - 生成时对值的转换函数，接收 (value, allValues) 返回最终值
 *   omitWhen      - 生成时省略此字段的条件函数，接收 (value, allValues) 返回 true 时省略
 */

/* ============================================================
 * 控件类型汇总：
 *   button-group      - 按钮组选择（分辨率、时长、宽高比、风格等）
 *   slider            - 滑块（时长范围等）
 *   switch            - 开关（音频生成、水印、提示词增强等）
 *   number-input      - 数字输入框（seed 等）
 *   size-grid         - 尺寸网格（图片比例选择，带可视化）
 *   upload            - 文件上传（参考图、尾帧图、参考视频等）
 *   text-input        - 文本输入（negativePrompt 等）
 *   card-select       - 卡片选择（模型版本、生成类型等）
 *   resolution-chips  - 分辨率芯片选择（1K/2K/4K 等）
 *   custom-size       - 自定义尺寸输入（宽 x 高）
 *   hidden            - 隐藏参数（有值但不在 UI 中显示，如固定的 modelId 映射）
 * ============================================================ */

var MODEL_PARAMS = {

  /* ============================================================
   * 视频模型（14 个）
   * ============================================================ */

  'wan-2-5': {
    type: 'video',
    modelId: 'wan-2-5',
    promptMaxlength: 2000,
    params: [
      {
        key: 'videoMode',
        type: 'button-group',
        label: 'modelIntro.common.videoMode',
        options: ['text', 'image'],
        default: 'text',
        showInCompact: false,
        optionLabels: { text: 'modelIntro.common.textToVideo', image: 'modelIntro.common.imageToVideo' }
      },
      {
        key: 'uploadedImage',
        type: 'upload',
        label: 'modelIntro.common.referenceImage',
        default: null,
        showInCompact: false,
        condition: function (v) { return v.videoMode === 'image' },
        props: { accept: 'image/png,image/jpeg,image/jpg', maxSize: 20 },
        mapTo: 'referenceImage',
        mapValue: function (val, v) { return v.videoMode === 'image' ? val : null }
      },
      {
        key: 'uploadedAudio',
        type: 'upload',
        label: 'modelIntro.common.referenceAudio',
        default: null,
        showInCompact: false,
        props: { accept: '.mp3,.wav,.m4a,.mp4' }
      },
      {
        key: 'negativePrompt',
        type: 'text-input',
        label: 'modelIntro.common.negativePrompt',
        default: '',
        showInCompact: false,
        props: { rows: 2, maxlength: 2000 }
      },
      {
        key: 'videoDuration',
        type: 'button-group',
        label: 'modelIntro.common.duration',
        options: [5],
        default: 5,
        showInCompact: true,
        optionLabels: { 5: '5s' }
      },
      {
        key: 'videoRes',
        type: 'button-group',
        label: 'modelIntro.common.resolution',
        options: ['480p', '720p', '1080p'],
        default: '480p',
        showInCompact: true
      },
      {
        key: 'aspectRatio',
        type: 'button-group',
        label: 'modelIntro.common.aspectRatio',
        options: ['1:1', '16:9', '9:16'],
        default: '16:9',
        showInCompact: false,
        condition: function (v) { return v.videoMode === 'text' },
        omitWhen: function (val, v) { return v.videoMode !== 'text' }
      },
      {
        key: 'seed',
        type: 'number-input',
        label: 'modelIntro.common.seed',
        default: 0,
        showInCompact: false
      },
      {
        key: 'wanAudio',
        type: 'hidden',
        default: true
      }
    ],
    buildParams: function (v) {
      var params = {
        audioUrl: v.uploadedAudio || undefined,
        wanAudio: v.wanAudio
      }
      return params
    }
  },

  'wan-2-6': {
    type: 'video',
    modelId: 'wan-2-6',
    promptMaxlength: 2000,
    params: [
      {
        key: 'videoMode',
        type: 'button-group',
        label: 'modelIntro.common.videoMode',
        options: ['text', 'image'],
        default: 'text',
        showInCompact: false,
        optionLabels: { text: 'modelIntro.common.textToVideo', image: 'modelIntro.common.imageToVideo' }
      },
      {
        key: 'uploadedImage',
        type: 'upload',
        label: 'modelIntro.common.referenceImage',
        default: null,
        showInCompact: false,
        condition: function (v) { return v.videoMode === 'image' },
        props: { accept: 'image/png,image/jpeg,image/jpg', maxSize: 20 },
        mapTo: 'referenceImage',
        mapValue: function (val, v) { return v.videoMode === 'image' ? val : null }
      },
      {
        key: 'uploadedAudio',
        type: 'upload',
        label: 'modelIntro.common.referenceAudio',
        default: null,
        showInCompact: false,
        props: { accept: '.mp3,.wav,.m4a,.mp4' }
      },
      {
        key: 'negativePrompt',
        type: 'text-input',
        label: 'modelIntro.common.negativePrompt',
        default: '',
        showInCompact: false,
        props: { rows: 2, maxlength: 2000 }
      },
      {
        key: 'videoDuration',
        type: 'button-group',
        label: 'modelIntro.common.duration',
        options: [5, 10, 15],
        default: 5,
        showInCompact: true,
        optionLabels: { 5: '5s', 10: '10s', 15: '15s' }
      },
      {
        key: 'videoRes',
        type: 'button-group',
        label: 'modelIntro.common.resolution',
        options: ['720p', '1080p'],
        default: '720p',
        showInCompact: true
      },
      {
        key: 'aspectRatio',
        type: 'button-group',
        label: 'modelIntro.common.aspectRatio',
        options: ['1:1', '4:3', '3:4', '16:9', '9:16'],
        default: '16:9',
        showInCompact: false,
        condition: function (v) { return v.videoMode === 'text' },
        omitWhen: function (val, v) { return v.videoMode !== 'text' }
      },
      {
        key: 'seed',
        type: 'number-input',
        label: 'modelIntro.common.seed',
        default: 0,
        showInCompact: false
      },
      {
        key: 'generateAudio',
        type: 'hidden',
        default: true
      }
    ],
    buildParams: function (v) {
      return {
        audioUrl: v.uploadedAudio || undefined,
        generateAudio: v.generateAudio
      }
    }
  },

  'wan-2-2': {
    type: 'video',
    modelId: 'wan-2-2',
    promptMaxlength: 800,
    params: [
      {
        key: 'wanMode',
        type: 'button-group',
        label: 'modelIntro.common.videoMode',
        options: ['text', 'image'],
        default: 'text',
        showInCompact: false,
        mapTo: 'videoMode',
        optionLabels: { text: 'modelIntro.common.textToVideo', image: 'modelIntro.common.imageToVideo' }
      },
      {
        key: 'uploadedImage',
        type: 'upload',
        label: 'modelIntro.common.referenceImage',
        default: null,
        showInCompact: false,
        condition: function (v) { return v.wanMode === 'image' },
        props: { accept: 'image/png,image/jpeg,image/jpg' },
        mapTo: 'referenceImage'
      },
      {
        key: 'negativePrompt',
        type: 'text-input',
        label: 'modelIntro.common.negativePrompt',
        default: '',
        showInCompact: false,
        props: { rows: 2, maxlength: 800 }
      },
      {
        key: 'videoDuration',
        type: 'button-group',
        label: 'modelIntro.common.duration',
        options: [5],
        default: 5,
        showInCompact: true,
        optionLabels: { 5: '5s' }
      },
      {
        key: 'videoRes',
        type: 'button-group',
        label: 'modelIntro.common.resolution',
        options: ['480p', '720p'],
        default: '480p',
        showInCompact: true,
        condition: function (v) { return v.wanMode === 'image' }
      },
      {
        key: 'videoResText',
        type: 'button-group',
        label: 'modelIntro.common.resolution',
        options: ['480p', '1080p'],
        default: '480p',
        showInCompact: true,
        condition: function (v) { return v.wanMode === 'text' },
        mapTo: 'videoRes'
      },
      {
        key: 'aspectRatio',
        type: 'button-group',
        label: 'modelIntro.common.aspectRatio',
        options: ['16:9', '9:16'],
        default: '16:9',
        showInCompact: false,
        condition: function (v) { return v.wanMode === 'text' },
        omitWhen: function (val, v) { return v.wanMode !== 'text' }
      },
      {
        key: 'seed',
        type: 'number-input',
        label: 'modelIntro.common.seed',
        default: 0,
        showInCompact: false
      }
    ],
    buildParams: function (v) {
      var res = v.wanMode === 'image' ? v.videoRes : v.videoResText
      return {
        videoRes: res || '480p'
      }
    }
  },

  'wan-animate': {
    type: 'video',
    modelId: 'wan-animate',
    promptMaxlength: 0,
    params: [
      {
        key: 'animateMode',
        type: 'card-select',
        label: 'modelIntro.wanAnimate.animateModeLabel',
        options: ['animate', 'replace'],
        default: 'animate',
        showInCompact: false,
        optionLabels: { animate: 'modelIntro.wanAnimate.modeAnimate', replace: 'modelIntro.wanAnimate.modeReplace' }
      },
      {
        key: 'uploadedImage',
        type: 'upload',
        label: 'modelIntro.common.referenceImage',
        default: null,
        required: true,
        showInCompact: false,
        props: { accept: 'image/jpeg,image/png,image/jpg' },
        mapTo: 'image'
      },
      {
        key: 'uploadedVideo',
        type: 'upload',
        label: 'modelIntro.wanAnimate.refVideoLabel',
        default: null,
        required: true,
        showInCompact: false,
        props: { accept: 'video/mp4' },
        mapTo: 'video'
      },
      {
        key: 'animateParamMode',
        type: 'button-group',
        label: 'modelIntro.wanAnimate.qualityLabel',
        options: ['std', 'pro'],
        default: 'std',
        showInCompact: true,
        optionLabels: { std: 'modelIntro.wanAnimate.qualityStd', pro: 'modelIntro.wanAnimate.qualityPro' },
        mapTo: 'mode',
        mapValue: function (val) {
          if (val === 'pro') return 'wan-pro'
          return 'wan-std'
        }
      },
      {
        key: 'watermark',
        type: 'switch',
        label: 'modelIntro.common.watermark',
        default: false,
        showInCompact: false
      }
    ],
    buildParams: function () {
      return {}
    }
  },

  'veo-3-0': {
    type: 'video',
    modelId: 'veo-3-0',
    promptMaxlength: 2000,
    params: [
      {
        key: 'videoMode',
        type: 'button-group',
        label: 'modelIntro.common.videoMode',
        options: ['text', 'image'],
        default: 'text',
        showInCompact: false,
        optionLabels: { text: 'modelIntro.common.textToVideo', image: 'modelIntro.common.imageToVideo' }
      },
      {
        key: 'uploadedImage',
        type: 'upload',
        label: 'modelIntro.common.referenceImage',
        default: null,
        showInCompact: false,
        condition: function (v) { return v.videoMode === 'image' },
        props: { accept: 'image/png,image/jpeg,image/jpg' },
        mapTo: 'referenceImage',
        mapValue: function (val, v) { return v.videoMode === 'image' ? (val || null) : null }
      },
      {
        key: 'negativePrompt',
        type: 'text-input',
        label: 'modelIntro.common.negativePrompt',
        default: '',
        showInCompact: false,
        props: { rows: 2, maxlength: 2000 }
      },
      {
        key: 'videoDuration',
        type: 'button-group',
        label: 'modelIntro.common.duration',
        options: [8],
        default: 8,
        showInCompact: true,
        optionLabels: { 8: '8s' }
      },
      {
        key: 'videoRes',
        type: 'button-group',
        label: 'modelIntro.common.resolution',
        options: ['1080p'],
        default: '1080p',
        showInCompact: true,
        optionLabels: { '1080p': '1080P' }
      },
      {
        key: 'aspectRatio',
        type: 'button-group',
        label: 'modelIntro.common.aspectRatio',
        options: ['16:9', '9:16'],
        default: '16:9',
        showInCompact: false
      },
      {
        key: 'generateAudio',
        type: 'hidden',
        default: true
      },
      {
        key: 'seed',
        type: 'number-input',
        label: 'modelIntro.common.seed',
        default: -1,
        showInCompact: false,
        mapValue: function (val) { var n = parseInt(val); return (!isNaN(n) && n >= 0) ? n : null }
      }
    ],
    buildParams: function () {
      return {}
    }
  },

  'veo-3-1': {
    type: 'video',
    modelId: 'veo-3-1',
    promptMaxlength: 2000,
    params: [
      {
        key: 'veoGenType',
        type: 'card-select',
        label: 'modelIntro.veo.imageMode',
        options: ['fast', 'quality'],
        default: 'fast',
        showInCompact: false,
        optionLabels: { fast: 'modelIntro.veo.fastType', quality: 'modelIntro.veo.qualityType' }
      },
      {
        key: 'videoMode',
        type: 'button-group',
        label: 'modelIntro.common.videoMode',
        options: ['text', 'image'],
        default: 'text',
        showInCompact: false,
        optionLabels: { text: 'modelIntro.common.textToVideo', image: 'modelIntro.common.imageToVideo' }
      },
      {
        key: 'uploadedImage',
        type: 'upload',
        label: 'modelIntro.common.referenceImage',
        default: null,
        showInCompact: false,
        condition: function (v) { return v.videoMode === 'image' },
        props: { accept: 'image/png,image/jpeg,image/jpg' },
        mapTo: 'referenceImage',
        mapValue: function (val, v) { return v.videoMode === 'image' ? (val || null) : null }
      },
      {
        key: 'endFrameImage',
        type: 'upload',
        label: 'modelIntro.veo.endFrame',
        default: null,
        showInCompact: false,
        condition: function (v) { return v.videoMode === 'image' },
        props: { accept: 'image/png,image/jpeg,image/jpg' },
        mapTo: 'imageTail',
        mapValue: function (val, v) { return v.videoMode === 'image' ? (val || null) : null }
      },
      {
        key: 'videoDuration',
        type: 'button-group',
        label: 'modelIntro.common.duration',
        options: [4, 6, 8],
        default: 8,
        showInCompact: true,
        optionLabels: { 4: '4s', 6: '6s', 8: '8s' }
      },
      {
        key: 'videoRes',
        type: 'button-group',
        label: 'modelIntro.common.resolution',
        options: ['720p', '1080p', '4K'],
        default: '1080p',
        showInCompact: true
      },
      {
        key: 'aspectRatio',
        type: 'button-group',
        label: 'modelIntro.common.aspectRatio',
        options: ['16:9', '9:16'],
        default: '16:9',
        showInCompact: false
      },
      {
        key: 'generateAudio',
        type: 'hidden',
        default: true
      },
      {
        key: 'seed',
        type: 'number-input',
        label: 'modelIntro.common.seed',
        default: -1,
        showInCompact: false,
        mapValue: function (val) { var n = parseInt(val); return (!isNaN(n) && n >= 0) ? n : null }
      }
    ],
    buildParams: function (v) {
      return {
        modelId: v.veoGenType === 'fast' ? 'veo-3-1-fast' : 'veo-3-1',
        veoGenType: v.veoGenType
      }
    }
  },

  'kling-2-6': {
    type: 'video',
    modelId: 'kling-2-6',
    promptMaxlength: 2500,
    params: [
      {
        key: 'videoMode',
        type: 'button-group',
        label: 'modelIntro.common.videoMode',
        options: ['text', 'image'],
        default: 'text',
        showInCompact: false,
        optionLabels: { text: 'modelIntro.common.textToVideo', image: 'modelIntro.common.imageToVideo' }
      },
      {
        key: 'uploadedImage',
        type: 'upload',
        label: 'modelIntro.common.referenceImage',
        default: null,
        showInCompact: false,
        condition: function (v) { return v.videoMode === 'image' },
        props: { accept: 'image/png,image/jpeg,image/jpg,image/webp', maxSize: 10 },
        mapTo: 'referenceImage',
        mapValue: function (val, v) { return v.videoMode === 'image' ? val : null }
      },
      {
        key: 'videoDuration',
        type: 'button-group',
        label: 'modelIntro.common.duration',
        options: [5, 10],
        default: 5,
        showInCompact: true,
        optionLabels: { 5: '5s', 10: '10s' }
      },
      {
        key: 'aspectRatio',
        type: 'button-group',
        label: 'modelIntro.common.aspectRatio',
        options: ['1:1', '16:9', '9:16'],
        default: '16:9',
        showInCompact: false,
        condition: function (v) { return v.videoMode === 'text' }
      },
      {
        key: 'generateAudio',
        type: 'switch',
        label: 'modelIntro.common.generateAudio',
        default: false,
        showInCompact: false
      }
    ],
    buildParams: function () {
      return {}
    }
  },

  'kling-2-5': {
    type: 'video',
    modelId: 'kling-2-5',
    promptMaxlength: 1500,
    params: [
      {
        key: 'videoMode',
        type: 'button-group',
        label: 'modelIntro.common.videoMode',
        options: ['text', 'image'],
        default: 'text',
        showInCompact: false,
        optionLabels: { text: 'modelIntro.common.textToVideo', image: 'modelIntro.common.imageToVideo' }
      },
      {
        key: 'uploadedImage',
        type: 'upload',
        label: 'modelIntro.common.referenceImage',
        default: null,
        showInCompact: false,
        condition: function (v) { return v.videoMode === 'image' },
        props: { accept: 'image/png,image/jpeg,image/jpg,image/webp', maxSize: 10 },
        mapTo: 'referenceImage',
        mapValue: function (val, v) { return v.videoMode === 'image' ? val : null }
      },
      {
        key: 'endFrameImage',
        type: 'upload',
        label: 'modelIntro.common.endFrame',
        default: null,
        showInCompact: false,
        condition: function (v) { return v.videoMode === 'image' },
        props: { accept: 'image/png,image/jpeg,image/jpg' }
      },
      {
        key: 'negativePrompt',
        type: 'text-input',
        label: 'modelIntro.common.negativePrompt',
        default: '',
        showInCompact: false,
        props: { rows: 2, maxlength: 1500 },
        mapValue: function (val) { return (val || '').trim() || undefined }
      },
      {
        key: 'videoDuration',
        type: 'button-group',
        label: 'modelIntro.common.duration',
        options: [5, 10],
        default: 5,
        showInCompact: true,
        optionLabels: { 5: '5s', 10: '10s' }
      },
      {
        key: 'aspectRatio',
        type: 'button-group',
        label: 'modelIntro.common.aspectRatio',
        options: ['1:1', '16:9', '9:16'],
        default: '16:9',
        showInCompact: false,
        condition: function (v) { return v.videoMode === 'text' }
      }
    ],
    buildParams: function (v) {
      return {
        endFrameImage: v.videoMode === 'image' ? (v.endFrameImage || null) : null
      }
    }
  },

  'kling-3': {
    type: 'video',
    modelId: 'kling-3',
    promptMaxlength: 2500,
    params: [
      {
        key: 'videoMode',
        type: 'button-group',
        label: 'modelIntro.common.videoMode',
        options: ['text', 'image'],
        default: 'text',
        showInCompact: false,
        optionLabels: { text: 'modelIntro.common.textToVideo', image: 'modelIntro.common.imageToVideo' }
      },
      {
        key: 'uploadedImage',
        type: 'upload',
        label: 'modelIntro.common.referenceImage',
        default: null,
        showInCompact: false,
        condition: function (v) { return v.videoMode === 'image' },
        props: { accept: 'image/png,image/jpeg,image/jpg,image/webp', maxSize: 10 },
        mapTo: 'referenceImage',
        mapValue: function (val, v) { return v.videoMode === 'image' ? val : null }
      },
      {
        key: 'videoDuration',
        type: 'button-group',
        label: 'modelIntro.common.duration',
        options: [5, 10],
        default: 5,
        showInCompact: true,
        optionLabels: { 5: '5s', 10: '10s' }
      },
      {
        key: 'aspectRatio',
        type: 'button-group',
        label: 'modelIntro.common.aspectRatio',
        options: ['1:1', '16:9', '9:16'],
        default: '16:9',
        showInCompact: false,
        condition: function (v) { return v.videoMode === 'text' }
      },
      {
        key: 'generateAudio',
        type: 'switch',
        label: 'modelIntro.common.generateAudio',
        default: false,
        showInCompact: false
      }
    ],
    buildParams: function () {
      return {}
    }
  },

  'seedance': {
    type: 'video',
    modelId: 'seedance',
    promptMaxlength: 2000,
    params: [
      {
        key: 'videoMode',
        type: 'button-group',
        label: 'modelIntro.common.videoMode',
        options: ['text', 'image'],
        default: 'text',
        showInCompact: false,
        optionLabels: { text: 'modelIntro.common.textToVideo', image: 'modelIntro.common.imageToVideo' }
      },
      {
        key: 'uploadedImage',
        type: 'upload',
        label: 'modelIntro.common.referenceImage',
        default: null,
        showInCompact: false,
        condition: function (v) { return v.videoMode === 'image' },
        props: { accept: 'image/png,image/jpeg,image/jpg' },
        mapTo: 'referenceImage'
      },
      {
        key: 'endFrameImage',
        type: 'upload',
        label: 'modelIntro.common.endFrame',
        default: null,
        showInCompact: false,
        condition: function (v) { return v.videoMode === 'image' },
        props: { accept: 'image/png,image/jpeg,image/jpg' }
      },
      {
        key: 'videoDuration',
        type: 'slider',
        label: 'modelIntro.common.duration',
        default: 5,
        showInCompact: false,
        props: { min: 4, max: 10, step: 1, unit: 's' }
      },
      {
        key: 'videoRes',
        type: 'button-group',
        label: 'modelIntro.common.resolution',
        options: ['480p', '720p'],
        default: '480p',
        showInCompact: true
      },
      {
        key: 'aspectRatio',
        type: 'button-group',
        label: 'modelIntro.common.aspectRatio',
        options: ['16:9', '9:16', '1:1'],
        default: '16:9',
        showInCompact: false,
        condition: function (v) { return v.videoMode !== 'extend' }
      },
      {
        key: 'generateAudio',
        type: 'switch',
        label: 'modelIntro.common.generateAudio',
        default: false,
        showInCompact: false
      },
      {
        key: 'seed',
        type: 'number-input',
        label: 'modelIntro.common.seed',
        default: -1,
        showInCompact: false
      }
    ],
    buildParams: function (v) {
      return {
        endFrameImage: v.endFrameImage,
        referenceImage: v.uploadedImage
      }
    }
  },

  'sora-2': {
    type: 'video',
    modelId: 'sora-2',
    promptMaxlength: 4000,
    params: [
      {
        key: 'videoMode',
        type: 'button-group',
        label: 'modelIntro.common.videoMode',
        options: ['text', 'image'],
        default: 'text',
        showInCompact: false,
        optionLabels: { text: 'modelIntro.common.textToVideo', image: 'modelIntro.common.imageToVideo' }
      },
      {
        key: 'uploadedImage',
        type: 'upload',
        label: 'modelIntro.common.referenceImage',
        default: null,
        showInCompact: false,
        condition: function (v) { return v.videoMode === 'image' },
        props: { accept: 'image/png,image/jpeg,image/jpg', maxSize: 20 },
        mapTo: 'referenceImage',
        mapValue: function (val, v) { return v.videoMode === 'image' ? val : null }
      },
      {
        key: 'videoDuration',
        type: 'button-group',
        label: 'modelIntro.common.duration',
        options: [4, 8, 12],
        default: 4,
        showInCompact: true,
        optionLabels: { 4: '4s', 8: '8s', 12: '12s' }
      },
      {
        key: 'videoRes',
        type: 'button-group',
        label: 'modelIntro.common.resolution',
        options: ['720p', '1080p'],
        default: '1080p',
        showInCompact: true
      },
      {
        key: 'aspectRatio',
        type: 'button-group',
        label: 'modelIntro.common.aspectRatio',
        options: ['16:9', '9:16'],
        default: '16:9',
        showInCompact: false
      }
    ],
    buildParams: function () {
      return {}
    }
  },

  'seegen': {
    type: 'video',
    modelId: 'seegen',
    promptMaxlength: 2000,
    params: [
      {
        key: 'videoMode',
        type: 'button-group',
        label: 'modelIntro.common.videoMode',
        options: ['text', 'image'],
        default: 'text',
        showInCompact: false,
        optionLabels: { text: 'modelIntro.common.textToVideo', image: 'modelIntro.common.imageToVideo' }
      },
      {
        key: 'seegenGenMode',
        type: 'button-group',
        label: 'modelIntro.seegen.genMode',
        options: ['stand', 'speed'],
        default: 'stand',
        showInCompact: true,
        optionLabels: { stand: 'modelIntro.seegen.modeStand', speed: 'modelIntro.seegen.modeSpeed' }
      },
      {
        key: 'uploadedImage',
        type: 'upload',
        label: 'modelIntro.common.referenceImage',
        default: null,
        showInCompact: false,
        condition: function (v) { return v.videoMode === 'image' },
        props: { accept: 'image/png,image/jpeg,image/jpg', maxSize: 20 },
        mapTo: 'referenceImage',
        mapValue: function (val, v) { return v.videoMode === 'image' ? val : null }
      },
      {
        key: 'videoDuration',
        type: 'slider',
        label: 'modelIntro.common.duration',
        default: 5,
        showInCompact: true,
        props: { min: 4, max: 15, step: 1, unit: 's' }
      },
      {
        key: 'videoRes',
        type: 'button-group',
        label: 'modelIntro.common.resolution',
        options: ['480p', '720p', '1080p', '2K', '4K'],
        default: '720p',
        showInCompact: true
      },
      {
        key: 'aspectRatio',
        type: 'button-group',
        label: 'modelIntro.common.aspectRatio',
        options: ['adaptive', '16:9', '9:16', '4:3', '3:4', '1:1', '21:9'],
        default: 'adaptive',
        showInCompact: false,
        optionLabels: {
          adaptive: 'modelIntro.seegen.aspectAdaptive',
          '16:9': '16:9',
          '9:16': '9:16',
          '4:3': '4:3',
          '3:4': '3:4',
          '1:1': '1:1',
          '21:9': '21:9'
        }
      },
      {
        key: 'generateAudio',
        type: 'switch',
        label: 'modelIntro.common.generateAudio',
        default: true,
        showInCompact: false
      },
      {
        key: 'watermark',
        type: 'switch',
        label: 'modelIntro.common.watermark',
        default: false,
        showInCompact: false
      }
    ],
    buildParams: function () {
      return {}
    }
  },

  'pollodance': {
    type: 'video',
    modelId: 'pollodance',
    promptMaxlength: 2000,
    params: [
      {
        key: 'pollodanceVariant',
        type: 'card-select',
        label: 'modelIntro.pollodance.variantLabel',
        options: ['2-0', '2-0-fast', '2-0-ref', '2-0-fast-ref'],
        default: '2-0',
        showInCompact: false,
        optionLabels: {
          '2-0': 'modelIntro.pollodance.card20',
          '2-0-fast': 'modelIntro.pollodance.card20Fast',
          '2-0-ref': 'modelIntro.pollodance.card20Ref',
          '2-0-fast-ref': 'modelIntro.pollodance.card20FastRef'
        },
        optionSubLabels: {
          '2-0': 'modelIntro.pollodance.opt20Desc',
          '2-0-fast': 'modelIntro.pollodance.opt20FastDesc',
          '2-0-ref': 'modelIntro.pollodance.opt20RefDesc',
          '2-0-fast-ref': 'modelIntro.pollodance.opt20FastRefDesc'
        }
      },
      {
        key: 'videoMode',
        type: 'button-group',
        label: 'modelIntro.common.videoMode',
        options: ['text', 'image'],
        default: 'text',
        showInCompact: false,
        condition: function (v) {
          return v.pollodanceVariant === '2-0' || v.pollodanceVariant === '2-0-fast'
        },
        optionLabels: { text: 'modelIntro.common.textToVideo', image: 'modelIntro.common.imageToVideo' }
      },
      {
        key: 'uploadedImage',
        type: 'upload',
        label: 'modelIntro.common.referenceImage',
        default: null,
        showInCompact: false,
        condition: function (v) {
          return (v.pollodanceVariant === '2-0' || v.pollodanceVariant === '2-0-fast') && v.videoMode === 'image'
        },
        props: { accept: 'image/png,image/jpeg,image/jpg', maxSize: 20 },
        mapTo: 'referenceImage',
        mapValue: function (val, v) {
          return (v.pollodanceVariant === '2-0' || v.pollodanceVariant === '2-0-fast') && v.videoMode === 'image' ? val : null
        }
      },
      {
        key: 'pollodanceRefImages',
        type: 'upload',
        label: 'modelIntro.pollodance.refImages',
        default: [],
        showInCompact: false,
        condition: function (v) {
          return v.pollodanceVariant === '2-0-ref' || v.pollodanceVariant === '2-0-fast-ref'
        },
        props: { accept: 'image/png,image/jpeg,image/jpg', maxCount: 8, multiUrl: true }
      },
      {
        key: 'pollodanceRefVideo',
        type: 'upload',
        label: 'modelIntro.pollodance.refVideo',
        default: null,
        showInCompact: false,
        condition: function (v) {
          return v.pollodanceVariant === '2-0-ref' || v.pollodanceVariant === '2-0-fast-ref'
        },
        props: { accept: 'video/mp4,video/quicktime,.mp4,.mov', maxSize: 100 }
      },
      {
        key: 'pollodanceRefAudio',
        type: 'upload',
        label: 'modelIntro.pollodance.refAudio',
        default: null,
        showInCompact: false,
        condition: function (v) {
          return v.pollodanceVariant === '2-0-ref' || v.pollodanceVariant === '2-0-fast-ref'
        },
        props: { accept: '.mp3,.wav,.m4a,audio/mpeg,audio/wav', maxSize: 20 }
      },
      {
        key: 'videoDuration',
        type: 'slider',
        label: 'modelIntro.common.duration',
        default: 10,
        showInCompact: true,
        props: { min: 4, max: 15, step: 1, unit: 's' }
      },
      {
        key: 'videoRes',
        type: 'button-group',
        label: 'modelIntro.common.resolution',
        options: ['480p', '720p', '1080p'],
        default: '720p',
        showInCompact: true,
        props: {
          disableOption: function (opt, v) {
            var pv = v.pollodanceVariant
            return String(opt) === '1080p' && (pv === '2-0-fast' || pv === '2-0-fast-ref')
          }
        }
      },
      {
        key: 'aspectRatio',
        type: 'button-group',
        label: 'modelIntro.common.aspectRatio',
        options: ['16:9', '4:3', '1:1', '3:4', '9:16', '21:9'],
        default: '16:9',
        showInCompact: false
      },
      {
        key: 'generateAudio',
        type: 'switch',
        label: 'modelIntro.common.generateAudio',
        default: true,
        showInCompact: false
      },
      {
        key: 'webSearch',
        type: 'switch',
        label: 'modelIntro.pollodance.webSearch',
        default: false,
        showInCompact: false
      },
      {
        key: 'seed',
        type: 'number-input',
        label: 'modelIntro.common.seed',
        default: 0,
        showInCompact: false,
        props: { min: 0, max: 2147483647, step: 1 }
      }
    ],
    buildParams: function () {
      return {}
    }
  },

  'hailuo-2-3': {
    type: 'video',
    modelId: 'hailuo-2-3',
    promptMaxlength: 2000,
    params: [
      {
        key: 'videoMode',
        type: 'button-group',
        label: 'modelIntro.common.videoMode',
        options: ['text', 'image'],
        default: 'text',
        showInCompact: false,
        optionLabels: { text: 'modelIntro.common.textToVideo', image: 'modelIntro.common.imageToVideo' }
      },
      {
        key: 'uploadedImage',
        type: 'upload',
        label: 'modelIntro.common.referenceImage',
        default: null,
        showInCompact: false,
        condition: function (v) { return v.videoMode === 'image' },
        props: { accept: 'image/png,image/jpeg,image/jpg', maxSize: 20 },
        mapTo: 'referenceImage',
        mapValue: function (val, v) { return v.videoMode === 'image' ? val : null }
      },
      {
        key: 'videoRes',
        type: 'button-group',
        label: 'modelIntro.common.resolution',
        options: ['768P', '1080P'],
        default: '768P',
        showInCompact: true
      },
      {
        key: 'videoDuration',
        type: 'button-group',
        label: 'modelIntro.common.duration',
        options: [6, 10],
        default: 6,
        showInCompact: true,
        optionLabels: { 6: '6s', 10: '10s' },
        props: { disableOption: function (opt, v) { return opt === 10 && v.videoRes === '1080P' } }
      },
      {
        key: 'promptEnhance',
        type: 'switch',
        label: 'modelIntro.common.promptEnhance',
        default: true,
        showInCompact: false,
        condition: function (v) { return v.videoMode === 'image' },
        mapValue: function (val, v) { return v.videoMode === 'image' ? val : false }
      }
    ],
    buildParams: function (v) {
      return {
        videoDuration: (v.videoRes === '1080P' && v.videoDuration === 10) ? 6 : v.videoDuration
      }
    }
  },

  'vidu-q3-pro': {
    type: 'video',
    modelId: 'vidu-q3-pro',
    promptMaxlength: 2000,
    params: [
      {
        key: 'videoMode',
        type: 'button-group',
        label: 'modelIntro.common.videoMode',
        options: ['text', 'image'],
        default: 'text',
        showInCompact: false,
        optionLabels: { text: 'modelIntro.common.textToVideo', image: 'modelIntro.common.imageToVideo' }
      },
      {
        key: 'uploadedImage',
        type: 'upload',
        label: 'modelIntro.common.referenceImage',
        default: null,
        showInCompact: false,
        condition: function (v) { return v.videoMode === 'image' },
        props: { accept: 'image/png,image/jpeg,image/jpg' },
        mapTo: 'referenceImage',
        mapValue: function (val, v) { return v.videoMode === 'image' ? val : null }
      },
      {
        key: 'aspectRatio',
        type: 'button-group',
        label: 'modelIntro.common.aspectRatio',
        options: ['16:9', '9:16', '1:1', '4:3', '3:4'],
        default: '16:9',
        showInCompact: true,
        condition: function (v) { return v.videoMode === 'text' },
        mapValue: function (val, v) { return v.videoMode === 'text' ? val : undefined }
      },
      {
        key: 'videoLength',
        type: 'slider',
        label: 'modelIntro.common.duration',
        default: 5,
        showInCompact: false,
        props: { min: 1, max: 16, step: 1, unit: 's' },
        mapTo: 'videoDuration'
      },
      {
        key: 'videoRes',
        type: 'button-group',
        label: 'modelIntro.common.resolution',
        options: ['540p', '720p', '1080p'],
        default: '720p',
        showInCompact: true
      },
      {
        key: 'generateAudio',
        type: 'switch',
        label: 'modelIntro.common.generateAudio',
        default: true,
        showInCompact: false
      },
      {
        key: 'seedStr',
        type: 'text-input',
        label: 'modelIntro.common.seed',
        default: '',
        showInCompact: false,
        props: { inputmode: 'numeric' },
        mapTo: 'seed',
        mapValue: function (val) { var n = parseInt(val); return (!isNaN(n) && n > 0) ? n : undefined }
      }
    ],
    buildParams: function () {
      return {}
    }
  },

  'runway': {
    type: 'video',
    modelId: 'runway',
    promptMaxlength: 1000,
    params: [
      {
        key: 'videoMode',
        type: 'hidden',
        default: 'image'
      },
      {
        key: 'uploadedImage',
        type: 'upload',
        label: 'modelIntro.common.referenceImage',
        default: null,
        showInCompact: false,
        props: { accept: 'image/png,image/jpeg,image/jpg', maxSize: 20 },
        mapTo: 'referenceImage'
      },
      {
        key: 'videoDuration',
        type: 'button-group',
        label: 'modelIntro.common.duration',
        options: [5, 10],
        default: 5,
        showInCompact: true,
        optionLabels: { 5: '5s', 10: '10s' }
      },
      {
        key: 'aspectRatio',
        type: 'button-group',
        label: 'modelIntro.common.aspectRatio',
        options: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
        default: '16:9',
        showInCompact: false
      },
      {
        key: 'seed',
        type: 'number-input',
        label: 'modelIntro.common.seed',
        default: 0,
        showInCompact: false
      }
    ],
    buildParams: function () {
      return {}
    }
  },

  /* ============================================================
   * 图片模型（13 个）
   * ============================================================ */

  'flux-pro-1-1-ultra': {
    type: 'image',
    modelId: 'flux-pro-1-1-ultra',
    promptMaxlength: 1000,
    params: [
      {
        key: 'imageSize',
        type: 'size-grid',
        label: 'modelIntro.common.imageSize',
        options: ['21:9', '16:9', '4:3', '3:2', '1:1', '2:3', '3:4', '9:16', '9:21'],
        default: '1:1',
        showInCompact: true,
        compactType: 'button-group',
        compactOptions: ['1:1', '16:9', '9:16'],
        mapTo: 'aspectRatio'
      },
      {
        key: 'imageCount',
        type: 'hidden',
        default: 1
      },
      {
        key: 'style',
        type: 'hidden',
        default: 'auto'
      }
    ],
    buildParams: function () {
      return {}
    }
  },

  'flux-schnell': {
    type: 'image',
    modelId: 'flux-schnell',
    promptMaxlength: 1000,
    params: [
      {
        key: 'imageSize',
        type: 'size-grid',
        label: 'modelIntro.common.imageSize',
        options: ['21:9', '16:9', '4:3', '3:2', '1:1', '2:3', '3:4', '9:16', '9:21'],
        default: '1:1',
        showInCompact: true,
        compactType: 'button-group',
        compactOptions: ['1:1', '16:9', '9:16'],
        mapTo: 'aspectRatio'
      },
      {
        key: 'outputStyle',
        type: 'button-group',
        label: 'modelIntro.common.style',
        options: ['auto', 'photographic', 'digital_art', 'anime'],
        optionLabels: {
          auto: 'modelIntro.fluxKontext.outputStyleLabel.auto',
          photographic: 'modelIntro.fluxKontext.outputStyleLabel.photographic',
          digital_art: 'modelIntro.fluxKontext.outputStyleLabel.digital_art',
          anime: 'modelIntro.fluxKontext.outputStyleLabel.anime'
        },
        default: 'auto',
        showInCompact: false,
        mapTo: 'style'
      }
    ],
    buildParams: function () {
      return {}
    }
  },

  'flux-2': {
    type: 'image',
    modelId: 'flux-2',
    promptMaxlength: 1000,
    params: [
      {
        key: 'selectedVariant',
        type: 'card-select',
        label: 'modelIntro.flux2.variantLabel',
        options: ['klein-04', 'klein-09', 'max', 'pro', 'flex'],
        default: 'pro',
        showInCompact: false,
        optionLabels: {
          'klein-04': 'Klein 4B',
          'klein-09': 'Klein 9B',
          'max': 'Max',
          'pro': 'Pro',
          'flex': 'Flex'
        },
        mapTo: 'variant'
      },
      {
        key: 'resolutionPreset',
        type: 'button-group',
        label: 'modelIntro.common.resolution',
        equalCols: 3,
        options: ['1024*1024', '1280*1280', '864*1536', '1536*864', '720*1280', '1280*720', '1024*1536', '1536*1536', 'custom'],
        default: '1024*1024',
        showInCompact: true,
        optionLabels: {
          custom: 'Custom'
        }
      },
      {
        key: 'customSize',
        type: 'custom-size',
        label: '',
        default: { width: 1024, height: 1024 },
        showInCompact: false,
        condition: function (v) { return v.resolutionPreset === 'custom' },
        props: { min: 512, max: 2048 },
        mapTo: 'resolution',
        mapValue: function (val) {
          if (!val || typeof val !== 'object') return undefined
          return val.width + '*' + val.height
        }
      },
      {
        key: 'promptExtend',
        type: 'switch',
        label: 'modelIntro.common.promptExtend',
        default: true,
        showInCompact: true
      },
      {
        key: 'seed',
        type: 'number-input',
        label: 'modelIntro.common.seed',
        default: 0,
        showInCompact: false,
        props: { min: 0, max: 2147483647 }
      }
    ],
    buildParams: function (v) {
      if (v.resolutionPreset && v.resolutionPreset !== 'custom') {
        return { resolution: v.resolutionPreset }
      }
      return {}
    }
  },

  'flux-kontext': {
    type: 'image',
    modelId: 'flux-kontext',
    promptMaxlength: 1000,
    params: [
      {
        key: 'imageSize',
        type: 'size-grid',
        label: 'modelIntro.common.imageSize',
        options: ['21:9', '16:9', '4:3', '3:2', '1:1', '2:3', '3:4', '9:16', '9:21'],
        default: '1:1',
        showInCompact: true,
        compactType: 'button-group',
        compactOptions: ['1:1', '16:9', '9:16'],
        mapTo: 'aspectRatio'
      },
      {
        key: 'imageCount',
        type: 'button-group',
        label: 'modelIntro.common.imageCount',
        options: [1, 2, 3, 4],
        default: 1,
        showInCompact: false,
        mapTo: 'count'
      }
    ],
    buildParams: function () {
      return {}
    }
  },

  'gpt-image-1': {
    type: 'image',
    modelId: 'gpt-image-1',
    promptMaxlength: 2000,
    params: [
      {
        key: 'imageSize',
        type: 'button-group',
        label: 'modelIntro.common.imageSize',
        options: ['1024x1024', '1536x1024', '1024x1536'],
        default: '1024x1024',
        showInCompact: true
      }
    ],
    buildParams: function () {
      return {}
    }
  },

  'gpt-image-1-5': {
    type: 'image',
    modelId: 'gpt-image-1-5',
    promptMaxlength: 2000,
    params: [
      {
        key: 'imageSize',
        type: 'button-group',
        label: 'modelIntro.common.imageSize',
        options: ['1024x1024', '1536x1024', '1024x1536'],
        default: '1024x1024',
        showInCompact: true
      },
      {
        key: 'quality',
        type: 'button-group',
        label: 'modelIntro.common.quality',
        options: ['auto', 'low', 'medium', 'high'],
        optionLabels: {
          auto: 'modelIntro.common.qualityAuto',
          low: 'modelIntro.common.qualityLow',
          medium: 'modelIntro.common.qualityMedium',
          high: 'modelIntro.common.qualityHigh'
        },
        default: 'auto',
        showInCompact: true
      }
    ],
    buildParams: function () {
      return {}
    }
  },

  'seedream-4': {
    type: 'image',
    modelId: 'seedream-4',
    promptMaxlength: 2000,
    params: [
      {
        key: 'sizeMode',
        type: 'button-group',
        label: 'modelIntro.common.sizeMode',
        options: ['resolution', 'custom'],
        optionLabels: {
          resolution: 'modelIntro.seedream5.sizeModeResolution',
          custom: 'modelIntro.seedream5.sizeModeCustom'
        },
        default: 'resolution',
        showInCompact: false
      },
      {
        key: 'resolutionPreset',
        type: 'resolution-chips',
        label: 'modelIntro.common.resolution',
        options: ['1K', '2K', '4K'],
        default: '2K',
        showInCompact: true,
        condition: function (v) { return v.sizeMode === 'resolution' }
      },
      {
        key: 'customPreset',
        type: 'button-group',
        label: 'modelIntro.common.resolution',
        equalCols: 3,
        options: ['1024*1024', '736*1312', '1312*736', '1664*2496', '3136*1344', 'custom'],
        default: '1024*1024',
        showInCompact: false,
        condition: function (v) { return v.sizeMode === 'custom' },
        optionLabels: {
          custom: 'Custom'
        }
      },
      {
        key: 'customSize',
        type: 'custom-size',
        label: '',
        default: { width: 1024, height: 1024 },
        showInCompact: false,
        condition: function (v) { return v.sizeMode === 'custom' && v.customPreset === 'custom' },
        props: { min: 1, max: 4096 },
        mapTo: 'resolution',
        mapValue: function (val) {
          if (!val || typeof val !== 'object') return undefined
          return val.width + '*' + val.height
        }
      },
      {
        key: 'addWatermark',
        type: 'switch',
        label: 'modelIntro.common.watermark',
        default: false,
        showInCompact: false
      }
    ],
    buildParams: function (v) {
      if (v.sizeMode === 'resolution' && v.resolutionPreset) {
        return { resolution: v.resolutionPreset }
      }
      if (v.sizeMode === 'custom' && v.customPreset && v.customPreset !== 'custom') {
        return { resolution: v.customPreset }
      }
      return {}
    }
  },

  'seedream-5': {
    type: 'image',
    modelId: 'seedream-5',
    promptMaxlength: 2000,
    params: [
      {
        key: 'sizeMode',
        type: 'button-group',
        label: 'modelIntro.common.sizeMode',
        options: ['resolution', 'custom'],
        optionLabels: {
          resolution: 'modelIntro.seedream5.sizeModeResolution',
          custom: 'modelIntro.seedream5.sizeModeCustom'
        },
        default: 'resolution',
        showInCompact: false
      },
      {
        key: 'resolutionPreset',
        type: 'resolution-chips',
        label: 'modelIntro.common.resolution',
        options: ['2K', '3K', '4K'],
        default: '2K',
        showInCompact: true,
        condition: function (v) { return v.sizeMode === 'resolution' }
      },
      {
        key: 'customPreset',
        type: 'button-group',
        label: 'modelIntro.common.resolution',
        equalCols: 3,
        options: ['2048*2048', '2304*4096', '4096*2304', '1664*2496', '3136*1344', 'custom'],
        default: '2048*2048',
        showInCompact: false,
        condition: function (v) { return v.sizeMode === 'custom' },
        optionLabels: {
          custom: 'Custom'
        }
      },
      {
        key: 'customSize',
        type: 'custom-size',
        label: '',
        default: { width: 2048, height: 2048 },
        showInCompact: false,
        condition: function (v) { return v.sizeMode === 'custom' && v.customPreset === 'custom' },
        props: { min: 1, max: 4096 },
        mapTo: 'resolution',
        mapValue: function (val) {
          if (!val || typeof val !== 'object') return undefined
          return val.width + '*' + val.height
        }
      },
      {
        key: 'addWatermark',
        type: 'switch',
        label: 'modelIntro.common.watermark',
        default: false,
        showInCompact: false
      }
    ],
    buildParams: function (v) {
      if (v.sizeMode === 'resolution' && v.resolutionPreset) {
        return { resolution: v.resolutionPreset }
      }
      if (v.sizeMode === 'custom' && v.customPreset && v.customPreset !== 'custom') {
        return { resolution: v.customPreset }
      }
      return {}
    }
  },

  'midjourney': {
    type: 'image',
    modelId: 'midjourney',
    promptMaxlength: 2000,
    params: [
      {
        key: 'styleOption',
        type: 'button-group',
        label: 'modelIntro.common.style',
        options: ['realistic', 'anime'],
        optionLabels: {
          realistic: 'modelIntro.midjourney.styleRealistic',
          anime: 'modelIntro.midjourney.styleAnime'
        },
        default: 'realistic',
        showInCompact: true,
        mapTo: 'style'
      },
      {
        key: 'referenceImages',
        type: 'upload',
        label: 'modelIntro.common.referenceImage',
        default: [],
        showInCompact: false,
        props: { accept: 'image/png,image/jpeg,image/jpg,image/webp', maxCount: 5, maxSize: 0.1, base64: true },
        mapTo: 'referenceImagesBase64'
      }
    ],
    buildParams: function () {
      return {}
    }
  },

  'nano-banana': {
    type: 'image',
    modelId: 'nano-banana',
    promptMaxlength: 2000,
    params: [
      {
        key: 'modelVersion',
        type: 'card-select',
        label: 'modelIntro.nanoBanana.version',
        options: ['standard', 'pro'],
        default: 'standard',
        showInCompact: false,
        optionLabels: { standard: 'Standard', pro: 'Pro' }
      },
      {
        key: 'imageSize',
        type: 'button-group',
        label: 'modelIntro.common.imageSize',
        options: ['auto', '1:1', '4:3', '3:4', '16:9', '9:16', '3:2', '2:3'],
        default: 'auto',
        showInCompact: true,
        compactOptions: ['auto', '1:1', '16:9']
      },
      {
        key: 'resolution',
        type: 'button-group',
        label: 'modelIntro.common.resolution',
        options: ['1K', '2K', '4K'],
        default: '1K',
        showInCompact: false,
        condition: function (v) { return v.modelVersion === 'pro' },
        mapValue: function (val, v) { return v.modelVersion === 'pro' ? val : null }
      }
    ],
    buildParams: function () {
      return {}
    }
  },

  'nano-banana-2': {
    type: 'image',
    modelId: 'nano-banana-2',
    promptMaxlength: 2000,
    params: [
      {
        key: 'genType',
        type: 'card-select',
        label: 'modelIntro.nanoBanana2.genType',
        options: ['v25', 'v31'],
        default: 'v31',
        showInCompact: false,
        optionLabels: { v25: 'v2.5', v31: 'v3.1' }
      },
      {
        key: 'uploadedImage',
        type: 'upload',
        label: 'modelIntro.common.referenceImage',
        default: null,
        showInCompact: false,
        props: { accept: 'image/png,image/jpeg,image/jpg' },
        mapTo: 'referenceImage',
        mapValue: function (val) { return val || null }
      },
      {
        key: 'aspectRatio',
        type: 'button-group',
        label: 'modelIntro.common.aspectRatio',
        options: ['1:1', '1:4', '1:8', '2:3', '3:2', '4:3', '3:4', '4:1', '4:5', '5:4', '8:1', '16:9', '9:16', '21:9'],
        default: '16:9',
        showInCompact: true,
        compactOptions: ['1:1', '16:9', '9:16']
      },
      {
        key: 'imageSize',
        type: 'button-group',
        label: 'modelIntro.common.resolution',
        options: ['1K', '2K', '4K'],
        default: '1K',
        showInCompact: false,
        condition: function (v) { return v.genType === 'v31' },
        mapValue: function (val, v) { return v.genType === 'v31' ? val : '1K' }
      }
    ],
    buildParams: function () {
      return {}
    }
  },

  'qwen-image-2-0-pro': {
    type: 'image',
    modelId: 'qwen-image-2-0-pro',
    promptMaxlength: 800,
    params: [
      {
        key: 'negativePrompt',
        type: 'text-input',
        label: 'modelIntro.common.negativePrompt',
        default: '',
        showInCompact: false,
        props: { rows: 2, maxlength: 500 }
      },
      {
        key: 'resolutionPreset',
        type: 'button-group',
        label: 'modelIntro.common.resolution',
        equalCols: 3,
        options: ['2688*1536', '1536*2688', '2048*2048', '2368*1728', '1728*2368', 'custom'],
        default: '2048*2048',
        showInCompact: true,
        optionLabels: {
          custom: 'Custom'
        }
      },
      {
        key: 'customSize',
        type: 'custom-size',
        label: '',
        default: { width: 2048, height: 2048 },
        showInCompact: false,
        condition: function (v) { return v.resolutionPreset === 'custom' },
        props: { min: 512, max: 2048 },
        mapTo: 'resolution',
        mapValue: function (val) {
          if (!val || typeof val !== 'object') return undefined
          return val.width + '*' + val.height
        }
      },
      {
        key: 'imageCount',
        type: 'button-group',
        label: 'modelIntro.common.imageCount',
        options: [1, 2, 3, 4],
        default: 1,
        showInCompact: true,
        mapTo: 'n'
      },
      {
        key: 'promptEnhance',
        type: 'switch',
        label: 'modelIntro.common.promptEnhance',
        default: false,
        showInCompact: false,
        mapTo: 'promptExtend'
      },
      {
        key: 'watermark',
        type: 'switch',
        label: 'modelIntro.common.watermark',
        default: false,
        showInCompact: false
      }
    ],
    buildParams: function (v) {
      if (v.resolutionPreset && v.resolutionPreset !== 'custom') {
        return { resolution: v.resolutionPreset }
      }
      return {}
    }
  },

  'z-image-turbo': {
    type: 'image',
    modelId: 'z-image-turbo',
    promptMaxlength: 800,
    params: [
      {
        key: 'resolutionPreset',
        type: 'button-group',
        label: 'modelIntro.common.resolution',
        equalCols: 3,
        options: ['1024*1024', '1280*1280', '864*1536', '1536*864', '720*1280', '1280*720', '1024*1536', '1536*1536', 'custom'],
        default: '1024*1024',
        showInCompact: true,
        optionLabels: {
          custom: 'Custom'
        }
      },
      {
        key: 'customSize',
        type: 'custom-size',
        label: '',
        default: { width: 1024, height: 1024 },
        showInCompact: false,
        condition: function (v) { return v.resolutionPreset === 'custom' },
        props: { min: 512, max: 2048 },
        mapTo: 'resolution',
        mapValue: function (val) {
          if (!val || typeof val !== 'object') return undefined
          return val.width + '*' + val.height
        }
      },
      {
        key: 'promptExtend',
        type: 'switch',
        label: 'modelIntro.common.promptExtend',
        default: false,
        showInCompact: true,
        compactType: 'button-group',
        compactOptions: [false, true],
        optionLabels: { 'false': 'OFF', 'true': 'ON' }
      },
      {
        key: 'seed',
        type: 'number-input',
        label: 'modelIntro.common.seed',
        default: 0,
        showInCompact: false,
        props: { min: 0, max: 2147483647 }
      }
    ],
    buildParams: function (v) {
      if (v.resolutionPreset && v.resolutionPreset !== 'custom') {
        return { resolution: v.resolutionPreset }
      }
      return {}
    }
  },

  'sd-3-5': {
    type: 'image',
    modelId: 'sd-3-5',
    promptMaxlength: 2000,
    params: [
      {
        key: 'negativePrompt',
        type: 'text-input',
        label: 'modelIntro.common.negativePrompt',
        default: '',
        showInCompact: false,
        props: { rows: 2, maxlength: 2000 }
      },
      {
        key: 'resolutionPreset',
        type: 'button-group',
        label: 'modelIntro.common.resolution',
        equalCols: 3,
        options: ['1024*1024', '1280*1280', '896*1536', '1536*896', '768*1344', '1344*768', '1024*1536', '1536*1536', 'custom'],
        default: '1024*1024',
        showInCompact: true,
        optionLabels: {
          custom: 'Custom'
        }
      },
      {
        key: 'customSize',
        type: 'custom-size',
        label: '',
        default: { width: 1024, height: 1024 },
        showInCompact: false,
        condition: function (v) { return v.resolutionPreset === 'custom' },
        props: { min: 512, max: 2048, step: 64, hint: 'modelIntro.common.multipleOf64Hint' },
        mapTo: 'resolution',
        mapValue: function (val) {
          if (!val || typeof val !== 'object') return undefined
          return val.width + '*' + val.height
        }
      }
    ],
    buildParams: function (v) {
      if (v.resolutionPreset && v.resolutionPreset !== 'custom') {
        return { resolution: v.resolutionPreset }
      }
      return {}
    }
  }
}

/**
 * 获取模型参数配置
 * @param {string} modelId - 模型 ID
 * @returns {object|null} 参数配置对象
 */
function getModelParams(modelId) {
  return MODEL_PARAMS[modelId] || null
}

/**
 * 获取所有视频模型的参数配置
 * @returns {object} { modelId: config, ... }
 */
function getVideoModelParams() {
  var result = {}
  var keys = Object.keys(MODEL_PARAMS)
  for (var i = 0; i < keys.length; i++) {
    if (MODEL_PARAMS[keys[i]].type === 'video') {
      result[keys[i]] = MODEL_PARAMS[keys[i]]
    }
  }
  return result
}

/**
 * 获取所有图片模型的参数配置
 * @returns {object} { modelId: config, ... }
 */
function getImageModelParams() {
  var result = {}
  var keys = Object.keys(MODEL_PARAMS)
  for (var i = 0; i < keys.length; i++) {
    if (MODEL_PARAMS[keys[i]].type === 'image') {
      result[keys[i]] = MODEL_PARAMS[keys[i]]
    }
  }
  return result
}

/**
 * 根据参数配置初始化默认值对象
 * @param {string} modelId - 模型 ID
 * @returns {object} { paramKey: defaultValue, ... }
 */
function getDefaultValues(modelId) {
  var config = MODEL_PARAMS[modelId]
  if (!config) return { prompt: '' }

  var values = { prompt: '' }
  var params = config.params
  for (var i = 0; i < params.length; i++) {
    values[params[i].key] = params[i].default
  }
  return values
}

/**
 * 根据参数配置和当前值，构建 API 请求的 params 对象
 * @param {string} modelId - 模型 ID
 * @param {object} values - 当前参数值 { paramKey: value, ... }
 * @returns {object} API 请求用的 params 对象
 */
function buildApiParams(modelId, values) {
  var config = MODEL_PARAMS[modelId]
  if (!config) return {}

  var promptMax = (typeof config.promptMaxlength === 'number') ? config.promptMaxlength : 2000
  var promptText = (values.prompt || '').trim()
  var params = {
    modelId: config.modelId,
    prompt: promptMax > 0 ? promptText.slice(0, promptMax) : '',
    type: config.type
  }

  var schemaParams = config.params
  for (var i = 0; i < schemaParams.length; i++) {
    var p = schemaParams[i]
    var val = values[p.key]

    // 检查是否应省略
    if (p.omitWhen && p.omitWhen(val, values)) continue

    // 跳过无 UI 且无特殊映射的 hidden 参数
    if (p.type === 'hidden' && !p.mapTo) {
      params[p.key] = val
      continue
    }

    // 确定目标字段名
    var targetKey = p.mapTo || p.key

    // 确定值
    var finalVal = val
    if (p.mapValue) {
      finalVal = p.mapValue(val, values)
    }

    // 如果 mapValue 返回 undefined 则跳过
    if (finalVal === undefined) continue

    params[targetKey] = finalVal
  }

  // 合并 buildParams 的额外字段
  if (config.buildParams) {
    var extra = config.buildParams(values)
    var extraKeys = Object.keys(extra)
    for (var j = 0; j < extraKeys.length; j++) {
      params[extraKeys[j]] = extra[extraKeys[j]]
    }
  }

  return params
}

export { MODEL_PARAMS, getModelParams, getVideoModelParams, getImageModelParams, getDefaultValues, buildApiParams }
export default MODEL_PARAMS
